import React, { useRef, useState } from 'react';
import { Upload, FileText, Loader2, Check, X, Scan, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SmartDocumentUpload = ({ 
    label,
    currentDocument, 
    token, 
    onDocumentUpdate,
    onDataExtracted,  // Callback to receive extracted data
    documentType = 'birth_certificate',  // birth_certificate, id_card, passport, photo
    endpoint = '/api/athletes/upload-and-scan',
    acceptedTypes = '.pdf,.jpg,.jpeg,.png',
    maxSizeMB = 25,
    disabled = false,
    testId = 'smart-document-upload',
    enableAiScan = true
}) => {
    const [uploading, setUploading] = useState(false);
    const [scanning, setScanning] = useState(false);
    const [scanComplete, setScanComplete] = useState(false);
    const fileInputRef = useRef(null);

    const handleUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file size
        if (file.size > maxSizeMB * 1024 * 1024) {
            toast.error(`File must be less than ${maxSizeMB}MB`);
            return;
        }

        setUploading(true);
        setScanComplete(false);
        const formData = new FormData();
        formData.append('file', file);

        try {
            if (enableAiScan) {
                // Use AI scanning endpoint
                setScanning(true);
                toast.info('Scanning document with AI...', { duration: 3000 });
                
                const response = await axios.post(
                    `${API_URL}${endpoint}?document_type=${documentType}`,
                    formData,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        },
                        timeout: 60000 // 60 second timeout for AI processing
                    }
                );
                
                if (response.data.document_url) {
                    onDocumentUpdate(response.data.document_url);
                }
                
                if (response.data.extracted_data && onDataExtracted) {
                    onDataExtracted(response.data.extracted_data);
                    setScanComplete(true);
                    toast.success('Document scanned! Details extracted automatically.', {
                        description: 'Please verify the auto-filled information.',
                        duration: 5000
                    });
                } else {
                    toast.success(`${label} uploaded successfully!`);
                }
            } else {
                // Use regular upload endpoint
                const response = await axios.post(
                    `${API_URL}/api/athletes/upload-document`,
                    formData,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
                
                if (response.data.url) {
                    onDocumentUpdate(response.data.url);
                    toast.success(`${label} uploaded successfully!`);
                }
            }
        } catch (error) {
            console.error('Document upload/scan failed:', error);
            const errorMsg = error.response?.data?.detail || `Failed to process ${label.toLowerCase()}`;
            toast.error(errorMsg);
        } finally {
            setUploading(false);
            setScanning(false);
        }
    };

    return (
        <div className="space-y-2">
            <Label className="text-xs tracking-widest uppercase text-muted-foreground flex items-center gap-2">
                {enableAiScan && <Sparkles className="w-3 h-3 text-yellow-400" />}
                {label}
                {enableAiScan && (
                    <span className="text-[10px] bg-yellow-500/20 text-yellow-400 px-1.5 py-0.5 rounded ml-1">
                        AI Scan
                    </span>
                )}
            </Label>
            <div className="flex items-center gap-4">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={acceptedTypes}
                    onChange={handleUpload}
                    className="hidden"
                    disabled={disabled || uploading}
                    data-testid={`${testId}-input`}
                />
                
                {currentDocument ? (
                    <div className={`flex items-center gap-2 p-3 flex-1 border ${
                        scanComplete 
                            ? 'bg-yellow-500/10 border-yellow-500/30' 
                            : 'bg-green-500/10 border-green-500/30'
                    }`}>
                        {scanComplete ? (
                            <Sparkles className="w-4 h-4 text-yellow-400" />
                        ) : (
                            <Check className="w-4 h-4 text-green-400" />
                        )}
                        <span className={`text-sm ${scanComplete ? 'text-yellow-400' : 'text-green-400'}`}>
                            {scanComplete ? 'Scanned & Uploaded' : 'Document uploaded'}
                        </span>
                        <a 
                            href={`${API_URL}${currentDocument}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline ml-auto"
                        >
                            View
                        </a>
                        {!disabled && (
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                    onDocumentUpdate('');
                                    setScanComplete(false);
                                }}
                                className="h-6 w-6 p-0 text-muted-foreground hover:text-red-400"
                            >
                                <X className="w-3 h-3" />
                            </Button>
                        )}
                    </div>
                ) : (
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading || disabled}
                        className={`border-white/10 bg-[#0a1628] flex-1 h-11 justify-start ${
                            enableAiScan ? 'hover:border-yellow-500/50' : ''
                        }`}
                        data-testid={`${testId}-btn`}
                    >
                        {uploading ? (
                            scanning ? (
                                <>
                                    <Scan className="w-4 h-4 mr-2 animate-pulse" />
                                    AI Scanning...
                                </>
                            ) : (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Uploading...
                                </>
                            )
                        ) : (
                            <>
                                {enableAiScan ? (
                                    <Sparkles className="w-4 h-4 mr-2 text-yellow-400" />
                                ) : (
                                    <Upload className="w-4 h-4 mr-2" />
                                )}
                                {enableAiScan ? `Scan & Upload ${label}` : `Upload ${label}`}
                            </>
                        )}
                    </Button>
                )}
            </div>
            <p className="text-xs text-muted-foreground">
                {enableAiScan ? (
                    <>AI will extract details automatically. Accepted: {acceptedTypes.replace(/\./g, '').toUpperCase()}. Max {maxSizeMB}MB</>
                ) : (
                    <>Accepted: {acceptedTypes.replace(/\./g, '').toUpperCase()}. Max {maxSizeMB}MB</>
                )}
            </p>
        </div>
    );
};

export default SmartDocumentUpload;
